/**
 * useCapabilities — the React binding for the frontend capability mirror (DEC-019 Step ③ Step 6).
 *
 * This is the SINGLE source of truth for role/org-position-gated UI. It exists to kill the recurring
 * flash-of-wrong-UI bug (the Edit button / Drafts nav briefly showing for a buyer): role used to be
 * resolved ad-hoc in every component with a default `useState`, so the wrong role's UI rendered for a
 * frame before the real role arrived.
 *
 * Anti-flash contract (consume it like this):
 *   const { loadedOnce, has } = useCapabilities();
 *   if (!loadedOnce) return <Skeleton/>;          // reserve space; do NOT read `has` yet
 *   return has(CATALOG_WRITE) ? <Edit/> : null;   // only trustworthy once loadedOnce
 *
 * `has()` returns false until `loadedOnce` — it NEVER default-allows, and consumers gate on
 * `loadedOnce` so they never see a default-deny flash either. `loadedOnce` flips true only after auth
 * AND account-type role AND org position AND organization AND label have all settled.
 *
 * `loadedOnce` is LATCHED: for a given signed-in identity it goes false → true exactly once and never
 * back. The upstream inputs do dip (LabelProvider refetches, the resolve effect re-runs), and letting
 * consumers follow those dips made them unmount and re-mount their whole subtree repeatedly — which is
 * how a guard could end up stranded mid-animation, rendering an invisible page that only a reload
 * cleared. The resolved `subject` is latched with it so a dip cannot default-deny an allowed user.
 * A change of identity resets both.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { subjectHas, type Capability, type Subject } from "@/lib/capabilities";

import { UserCacheService } from "@/lib/user-cache";
import { useAuth } from "@/providers/AuthProvider";
import { useLabel } from "@/providers/LabelProvider";

/**
 * A resolution of the role fields, tagged with the user id it was fetched for.
 * `resolvedFor: null` is the resolved "no session" answer, not an unresolved one.
 */
interface ResolvedSubject {
  resolvedFor: string | null;
  accountType: string | null;
  orgRole: string | null;
  organizationId: string | null;
}

export interface CapabilitiesResult {
  /** True only once the full subject (auth + role + org_role + label) has resolved. Gate UI on this. */
  loadedOnce: boolean;
  /** The resolved subject. Trust its fields only when `loadedOnce`. */
  subject: Subject;
  /** Holds the capability? Returns false until `loadedOnce` (never default-allow). */
  has: (cap: Capability) => boolean;
  hasAny: (...caps: Capability[]) => boolean;
  hasAll: (...caps: Capability[]) => boolean;
}

/**
 * A first-render resolution for a user whose roles are already in the 60s cache.
 *
 * Without this, every freshly-mounted consumer rendered unresolved for at least one
 * commit — the three cache accessors are `async` even on a hit — so a navigation
 * that crossed a guard boundary (/tracks -> /upload swaps SellerGuard for
 * NonNegotiatorGuard at the same route position, which React remounts) showed a
 * full 300px Zen mark and a 520ms dissolve for a sub-millisecond Map read.
 *
 * Returns null unless ALL THREE fields are cached and fresh; a partial seed would
 * resolve the guard against an incomplete subject, which is the exact flash this
 * hook exists to prevent.
 */
function seedFromCache(userId: string | null): ResolvedSubject | null {
  if (!userId) return null;
  const seed = UserCacheService.getResolvedRolesSync(userId);
  return seed ? { resolvedFor: userId, ...seed } : null;
}

export function useCapabilities(): CapabilitiesResult {
  const { user, loading: authLoading } = useAuth();
  const { label, isLoading: labelLoading } = useLabel();
  const currentUserId = user?.id ?? null;

  // One atomic record, TAGGED with the user it describes. `null` = nothing resolved yet.
  //
  // This used to be three separate `string | null | undefined` states. Three states cannot be
  // committed atomically, and — more importantly — none of them recorded WHOSE they were. On a
  // same-tab account switch the incoming user rendered against the outgoing user's still-present
  // role for at least one frame, and (once loadedOnce became latched) for as long as their own
  // lookups took. `resolvedFor` is what closes that window: state is only ever trusted for the
  // identity it was fetched for.
  const [resolved, setResolved] = useState<ResolvedSubject | null>(() =>
    seedFromCache(user?.id ?? null)
  );

  const resolveFor = useCallback(async (uid: string, signal: { cancelled: boolean }) => {
    try {
      const [role, org, profile] = await Promise.all([
        UserCacheService.getUserRole(uid),
        UserCacheService.getOrgRole(uid),
        UserCacheService.getProfile(uid).catch(() => null),
      ]);
      if (signal.cancelled) return;
      setResolved({
        resolvedFor: uid,
        accountType: role ?? null,
        orgRole: org ?? null,
        organizationId: profile?.organization_id ?? null,
      });
    } catch {
      if (signal.cancelled) return;
      // On error, resolve to default-deny rather than hang on a skeleton forever.
      setResolved({ resolvedFor: uid, accountType: null, orgRole: null, organizationId: null });
    }
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };

    // Mirror LabelProvider's guard: while auth is still loading, resolve NOTHING (don't trust a stale
    // 60s role cache before we even know who the user is).
    if (authLoading) {
      setResolved(null);
      return;
    }
    // No authenticated user → resolved to "no role" (default-deny), and we are done loading.
    if (!currentUserId) {
      setResolved({ resolvedFor: null, accountType: null, orgRole: null, organizationId: null });
      return;
    }

    // Deliberately NOT clearing `resolved` when it already describes this same
    // user: the seed is as fresh as the cache the async path is about to read, so
    // blanking it would re-open the loading state for one commit and put the mark
    // back on screen for exactly the navigations the seed exists to smooth.
    setResolved((prev) => (prev?.resolvedFor === currentUserId ? prev : null));
    void resolveFor(currentUserId, signal);

    return () => {
      signal.cancelled = true;
    };
  }, [currentUserId, authLoading, resolveFor]);

  // Re-resolve when the profile changes (role/org_role can change post-onboarding); the existing
  // `profileUpdated` window event already fires from the profile save path. This deliberately does
  // NOT clear `resolved` first — the old values stay on screen until the new ones land, so saving a
  // profile does not blink every capability-gated control in the app.
  useEffect(() => {
    const handler = () => {
      if (!currentUserId) return;
      UserCacheService.clearCache(currentUserId);
      void resolveFor(currentUserId, { cancelled: false });
    };
    window.addEventListener("profileUpdated", handler);
    return () => window.removeEventListener("profileUpdated", handler);
  }, [currentUserId, resolveFor]);

  const liveSubject = useMemo<Subject>(
    () => ({
      userId: currentUserId,
      accountType: resolved?.accountType ?? null,
      orgRole: resolved?.orgRole ?? null,
      organizationId: resolved?.organizationId ?? null,
      labelTag: label?.id ?? null,
    }),
    [currentUserId, resolved, label?.id]
  );

  // Settled only when auth + label have finished AND we hold a resolution for THIS user.
  const settled =
    !authLoading &&
    !labelLoading &&
    resolved !== null &&
    resolved.resolvedFor === currentUserId;

  // …and then LATCHED, which is what the name has always promised. `settled` is a
  // plain expression recomputed on every render, so it follows its inputs back
  // down: `labelLoading` flips true again whenever LabelProvider refetches, and
  // the resolve effect clears `resolved` every time it re-runs. Each of those dips
  // took consumers from "resolved" back to "loading" and back again.
  //
  // That flapping is not cosmetic. Every consumer branches on this flag to render
  // a loader OR the content — two different element types — so a dip unmounts and
  // re-mounts the entire page subtree, throwing away its state mid-flight. It is
  // what produced the blank-page bug: a guard re-mounting while its entrance fade
  // was still running left the content parked at opacity 0 permanently.
  const latched = useRef(false);
  const lastResolved = useRef<Subject | null>(null);

  // The latch is per-identity, and MUST be. A same-tab account switch keeps these
  // components mounted, so without this reset the incoming user would inherit the
  // outgoing user's capabilities until their own resolved — the cross-user leak
  // AuthProvider already guards against on the cache side.
  const latchedUserId = useRef<string | null | undefined>(undefined);
  if (latchedUserId.current !== currentUserId) {
    latchedUserId.current = currentUserId;
    latched.current = false;
    lastResolved.current = null;
  }

  if (settled) {
    latched.current = true;
  }
  const loadedOnce = latched.current;

  // The subject is latched alongside the flag, and for the same reason. During a
  // re-resolve `resolved` is null, which `liveSubject` reads as a null role.
  // Serving that transient null while `loadedOnce` stays true would default-deny
  // a user who is in fact allowed —
  // reintroducing the flash-of-wrong-UI this hook exists to prevent (a seller
  // watching their Edit button vanish and come back). So consumers keep seeing
  // the last fully-resolved subject until a new one is fully resolved — and only
  // ever for the identity it was resolved for, per the reset above.
  if (settled) {
    lastResolved.current = liveSubject;
  }
  const subject = settled ? liveSubject : lastResolved.current ?? liveSubject;

  return useMemo<CapabilitiesResult>(() => {
    const has = (cap: Capability) => (loadedOnce ? subjectHas(subject, cap) : false);
    return {
      loadedOnce,
      subject,
      has,
      hasAny: (...caps: Capability[]) => caps.some(has),
      hasAll: (...caps: Capability[]) => caps.every(has),
    };
  }, [loadedOnce, subject]);
}
